import { useState } from "react"
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query"
import {
  AlertCircle,
  Copy,
  Loader2,
  Mail,
  Trash2,
  UserPlus,
  Users,
} from "lucide-react"

import { Button } from "@/components/ui/button"
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card"
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "@/components/ui/dialog"
import { Input } from "@/components/ui/input"
import { useToast } from "@/hooks/use-toast"
import { apiRequest } from "@/lib/api"
import type { ApiError } from "@/lib/api"
import type { Account, AccountInvitation, AccountMember } from "@/types"

type MembersPageProps = {
  account: Account
}

type MembersResponse = { members: AccountMember[] }
type InvitationsResponse = { invitations: AccountInvitation[] }
type InvitationCreateResponse = { invitation: AccountInvitation }

const formatDate = (dateString: string) => {
  const date = new Date(dateString)
  if (Number.isNaN(date.getTime())) return "—"
  return new Intl.DateTimeFormat("pt-BR", {
    day: "numeric",
    month: "short",
    year: "numeric",
  }).format(date)
}

const MembersPage = ({ account }: MembersPageProps) => {
  const queryClient = useQueryClient()
  const { toast } = useToast()
  const [inviteEmail, setInviteEmail] = useState("")
  const [isDialogOpen, setIsDialogOpen] = useState(false)

  const membersQuery = useQuery<MembersResponse, ApiError>({
    queryKey: ["account-members"],
    queryFn: () => apiRequest<MembersResponse>("/api/account_members"),
  })

  const invitationsQuery = useQuery<InvitationsResponse, ApiError>({
    queryKey: ["account-invitations"],
    queryFn: () => apiRequest<InvitationsResponse>("/api/invitations"),
  })

  const inviteMutation = useMutation<InvitationCreateResponse, ApiError>({
    mutationFn: () =>
      apiRequest<InvitationCreateResponse>("/api/invitations", {
        method: "POST",
        body: JSON.stringify({ invitation: { email: inviteEmail.trim().toLowerCase() } }),
      }),
    onSuccess: (data) => {
      setInviteEmail("")
      setIsDialogOpen(false)
      queryClient.invalidateQueries({ queryKey: ["account-invitations"] })
      toast({
        title: "Convite enviado",
        description: `Link de convite gerado para ${data.invitation.email}.`,
      })
    },
  })

  const removeMemberMutation = useMutation<void, ApiError, number>({
    mutationFn: (memberId) =>
      apiRequest(`/api/account_members/${memberId}`, { method: "DELETE" }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["account-members"] })
      toast({ title: "Membro removido", description: "O usuário foi removido da conta." })
    },
  })

  const revokeInvitationMutation = useMutation<void, ApiError, number>({
    mutationFn: (invitationId) =>
      apiRequest(`/api/invitations/${invitationId}`, { method: "DELETE" }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["account-invitations"] })
      toast({ title: "Convite revogado", description: "O convite foi cancelado." })
    },
  })

  const handleInvite = (e: React.FormEvent) => {
    e.preventDefault()
    if (!inviteEmail.trim()) return
    inviteMutation.mutate()
  }

  const copyInviteLink = (token: string) => {
    const link = `${window.location.origin}/sign-up?invitation=${token}`
    navigator.clipboard.writeText(link)
    toast({ title: "Link copiado", description: "O link de convite foi copiado para a área de transferência." })
  }

  const members = membersQuery.data?.members ?? []
  const invitations = invitationsQuery.data?.invitations ?? []
  const isLoading = membersQuery.isLoading || invitationsQuery.isLoading

  return (
    <div className="mx-auto flex w-full max-w-4xl flex-col gap-6 pb-10">
      <div className="flex flex-wrap items-center justify-between gap-4">
        <div>
          <h1 className="text-3xl font-bold tracking-tight">Membros</h1>
          <p className="mt-1 text-base text-muted-foreground">
            Gerencie quem tem acesso à conta <strong>{account.name}</strong>
          </p>
        </div>

        <Dialog open={isDialogOpen} onOpenChange={setIsDialogOpen}>
          <DialogTrigger asChild>
            <Button size="lg" className="gap-2">
              <UserPlus className="h-5 w-5" />
              Convidar Membro
            </Button>
          </DialogTrigger>
          <DialogContent className="sm:max-w-[460px]">
            <form onSubmit={handleInvite}>
              <DialogHeader>
                <DialogTitle>Convidar novo membro</DialogTitle>
                <DialogDescription>
                  Informe o e-mail do usuário. Será gerado um link de convite válido por 7 dias.
                </DialogDescription>
              </DialogHeader>
              <div className="py-6">
                <Input
                  type="email"
                  placeholder="email@exemplo.com"
                  value={inviteEmail}
                  onChange={(e) => setInviteEmail(e.target.value)}
                  autoFocus
                  className="text-base"
                />
                {inviteMutation.isError ? (
                  <p className="mt-2 text-sm text-destructive">
                    {inviteMutation.error.message}
                  </p>
                ) : null}
              </div>
              <DialogFooter>
                <Button
                  type="button"
                  variant="outline"
                  onClick={() => setIsDialogOpen(false)}
                  disabled={inviteMutation.isPending}
                >
                  Cancelar
                </Button>
                <Button
                  type="submit"
                  disabled={inviteMutation.isPending || !inviteEmail.trim()}
                >
                  {inviteMutation.isPending ? (
                    <>
                      <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                      Criando...
                    </>
                  ) : (
                    <>
                      <Mail className="mr-2 h-4 w-4" />
                      Gerar Convite
                    </>
                  )}
                </Button>
              </DialogFooter>
            </form>
          </DialogContent>
        </Dialog>
      </div>

      {isLoading ? (
        <div className="flex items-center justify-center py-12">
          <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
        </div>
      ) : null}

      {(membersQuery.isError || invitationsQuery.isError) ? (
        <div className="rounded-lg border border-red-200 bg-red-50 p-4">
          <div className="flex items-center gap-2">
            <AlertCircle className="h-4 w-4 text-red-600" />
            <p className="text-sm text-red-800">Falha ao carregar dados da conta.</p>
          </div>
        </div>
      ) : null}

      {!isLoading ? (
        <>
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Users className="h-5 w-5 text-primary" />
                Membros Ativos
              </CardTitle>
              <CardDescription>
                {members.length} {members.length === 1 ? "membro" : "membros"} na conta
              </CardDescription>
            </CardHeader>
            <CardContent>
              {members.length === 0 ? (
                <p className="text-sm text-muted-foreground">Nenhum membro encontrado.</p>
              ) : (
                <div className="space-y-3">
                  {members.map((member) => (
                    <div
                      key={member.id}
                      className="flex items-center justify-between rounded-lg border p-4"
                    >
                      <div className="min-w-0 flex-1">
                        <div className="flex items-center gap-2">
                          <p className="text-sm font-medium truncate">{member.email}</p>
                          {member.is_current_user ? (
                            <span className="rounded-full bg-primary/10 px-2 py-0.5 text-[10px] font-semibold text-primary">
                              Você
                            </span>
                          ) : null}
                        </div>
                        <p className="text-xs text-muted-foreground">
                          Desde {formatDate(member.joined_at)}
                        </p>
                      </div>
                      {!member.is_current_user ? (
                        <Button
                          variant="ghost"
                          size="sm"
                          className="text-destructive hover:text-destructive"
                          onClick={() => removeMemberMutation.mutate(member.id)}
                          disabled={removeMemberMutation.isPending}
                        >
                          <Trash2 className="h-4 w-4" />
                        </Button>
                      ) : null}
                    </div>
                  ))}
                </div>
              )}
            </CardContent>
          </Card>

          {invitations.length > 0 ? (
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <Mail className="h-5 w-5 text-primary" />
                  Convites Pendentes
                </CardTitle>
                <CardDescription>
                  {invitations.length} {invitations.length === 1 ? "convite pendente" : "convites pendentes"}
                </CardDescription>
              </CardHeader>
              <CardContent>
                <div className="space-y-3">
                  {invitations.map((invitation) => (
                    <div
                      key={invitation.id}
                      className="flex items-center justify-between rounded-lg border border-dashed p-4"
                    >
                      <div className="min-w-0 flex-1">
                        <p className="text-sm font-medium truncate">{invitation.email}</p>
                        <p className="text-xs text-muted-foreground">
                          Convidado por {invitation.invited_by_email} · Expira em {formatDate(invitation.expires_at)}
                        </p>
                      </div>
                      <div className="flex items-center gap-1">
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => copyInviteLink(invitation.token)}
                          title="Copiar link de convite"
                        >
                          <Copy className="h-4 w-4" />
                        </Button>
                        <Button
                          variant="ghost"
                          size="sm"
                          className="text-destructive hover:text-destructive"
                          onClick={() => revokeInvitationMutation.mutate(invitation.id)}
                          disabled={revokeInvitationMutation.isPending}
                          title="Revogar convite"
                        >
                          <Trash2 className="h-4 w-4" />
                        </Button>
                      </div>
                    </div>
                  ))}
                </div>
              </CardContent>
            </Card>
          ) : null}
        </>
      ) : null}
    </div>
  )
}

export default MembersPage
